/**
 * SharedWithMeList Component
 * Displays parlays that have been shared with the current user
 * Includes Copy to Builder functionality
 */

import LoadingSpinner from '@components/ui/LoadingSpinner';
import type { ReceivedShares, ShareWithMe } from '@hooks/useSharing';
import { type BuilderLeg, sharedLegToBuilderLeg } from '@parlay-picker/shared/builderLeg';
import { formatTimeAgo } from '@parlay-picker/shared/formatting';
import { useParlayBuilderStore } from '@stores/parlayBuilderStore';
import { AlertCircle, Check, Clock, Copy, Eye, User } from 'lucide-react';
import {
  type ReactElement,
  type RefCallback,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

// =============================================================================
// Helper Functions
// =============================================================================

function getResultBadgeStyle(result: string): string {
  switch (result) {
    case 'won':
      return 'bg-theme-success/20 text-theme-success border-theme-success/30';
    case 'lost':
      return 'bg-theme-danger/20 text-theme-danger border-theme-danger/30';
    case 'void':
      return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    case 'push':
      return 'bg-theme-surface-hover/20 text-theme-text-secondary border-gray-500/30';
    default:
      return 'bg-theme-warning/20 text-theme-warning border-theme-warning/30';
  }
}

// =============================================================================
// Sub-components
// =============================================================================

interface SharedParlayCardProps {
  share: ShareWithMe;
  onView: (shareId: number) => void;
  /** The card a notification link opened: ringed, and scrolled to through this ref */
  highlightRef?: RefCallback<HTMLDivElement>;
}

function SharedParlayCard({ share, onView, highlightRef }: SharedParlayCardProps): ReactElement {
  const isNew = share.viewedAt === null;
  const isPending = share.result === 'pending';
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'partial' | 'failed'>('idle');
  const addAllLegs = useParlayBuilderStore((s) => s.addAllLegs);
  const setExpanded = useParlayBuilderStore((s) => s.setExpanded);

  const handleClick = () => {
    if (isNew) {
      onView(share.shareId);
    }
  };

  const handleCopyToBuilder = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent marking as viewed

    // Convert legs to builder format
    const builderLegs = share.legs
      .map(sharedLegToBuilderLeg)
      .filter((leg): leg is BuilderLeg => leg !== null);

    if (builderLegs.length === 0) {
      setCopyStatus('failed');
      setTimeout(() => setCopyStatus('idle'), 2000);
      return;
    }

    // Add to builder
    const result = addAllLegs(builderLegs);

    if (result.addedCount === 0) {
      setCopyStatus('failed');
    } else if (result.blockedCount > 0) {
      setCopyStatus('partial');
    } else {
      setCopyStatus('copied');
    }

    // Expand the builder panel to show the added legs
    setExpanded(true);

    // Reset status after delay
    setTimeout(() => setCopyStatus('idle'), 2000);
  };

  const getCopyButtonContent = () => {
    switch (copyStatus) {
      case 'copied':
        return (
          <>
            <Check className="w-3.5 h-3.5" />
            <span>Copied!</span>
          </>
        );
      case 'partial':
        return (
          <>
            <AlertCircle className="w-3.5 h-3.5" />
            <span>Some added</span>
          </>
        );
      case 'failed':
        return (
          <>
            <AlertCircle className="w-3.5 h-3.5" />
            <span>Can't copy</span>
          </>
        );
      default:
        return (
          <>
            <Copy className="w-3.5 h-3.5" />
            <span>Copy to Builder</span>
          </>
        );
    }
  };

  const getCopyButtonStyle = () => {
    switch (copyStatus) {
      case 'copied':
        return 'bg-theme-success/20 text-theme-success border-theme-success/30';
      case 'partial':
        return 'bg-theme-warning/20 text-theme-warning border-theme-warning/30';
      case 'failed':
        return 'bg-theme-danger/20 text-theme-danger border-theme-danger/30';
      default:
        return 'bg-theme-primary/10 text-theme-primary border-theme-primary/30 hover:bg-theme-primary/20';
    }
  };

  return (
    <div
      ref={highlightRef}
      onClick={handleClick}
      data-testid="shared-parlay-card"
      data-highlighted={highlightRef ? 'true' : undefined}
      className={`bg-theme-surface/50 rounded-lg p-4 border transition-all cursor-pointer hover:bg-theme-surface ${
        highlightRef
          ? 'border-theme-primary/50 ring-2 ring-theme-primary/50'
          : isNew
            ? 'border-theme-primary/50 ring-1 ring-blue-500/30'
            : 'border-theme-border'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {/* User avatar placeholder */}
          <div className="w-8 h-8 rounded-full bg-theme-surface-hover flex items-center justify-center">
            <User className="w-4 h-4 text-theme-text-secondary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-200">
                {share.sharedBy.displayName || 'User'}
              </span>
              {isNew && (
                <span className="text-xs px-1.5 py-0.5 bg-theme-primary-fill text-theme-on-primary-fill rounded font-medium">
                  NEW
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs text-theme-text-tertiary">
              <Clock className="w-3 h-3" />
              <span>{formatTimeAgo(share.sharedAt)}</span>
            </div>
          </div>
        </div>

        {/* Result badge */}
        <span
          className={`text-xs px-2 py-0.5 rounded border font-medium ${getResultBadgeStyle(share.result)}`}
        >
          {share.result.toUpperCase()}
        </span>
      </div>

      {/* Parlay name */}
      {share.parlayName && (
        <div className="text-sm font-medium text-theme-text-secondary mb-2">{share.parlayName}</div>
      )}

      {/* Legs */}
      <div className="space-y-1.5 mb-3">
        {share.legs.slice(0, 3).map((leg, i) => (
          <div key={i} className="text-sm text-theme-text-secondary flex items-center gap-2">
            <span className="text-theme-text-tertiary">•</span>
            <span>
              {leg.player} {leg.prop}
            </span>
            {leg.odds && <span className="text-theme-text-tertiary">({leg.odds})</span>}
          </div>
        ))}
        {share.legs.length > 3 && (
          <div className="text-xs text-theme-text-tertiary">
            +{share.legs.length - 3} more leg
            {share.legs.length - 3 > 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-theme-text-tertiary">
          {share.legs.length} legs · {share.totalOdds}
        </span>
        <div className="flex items-center gap-2">
          {share.viewedAt && (
            <div className="flex items-center gap-1 text-xs text-theme-text-tertiary">
              <Eye className="w-3 h-3" />
              <span>Viewed</span>
            </div>
          )}
          {/* Copy to Builder button - only for pending parlays */}
          {isPending && (
            <button
              type="button"
              onClick={handleCopyToBuilder}
              disabled={copyStatus !== 'idle'}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded border transition-colors ${getCopyButtonStyle()}`}
            >
              {getCopyButtonContent()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

interface SharedWithMeListProps {
  /** App's copy: viewing a share here clears every share badge */
  receivedShares: ReceivedShares;
  /** The shared parlay a notification link opened */
  highlightParlayId?: number | null;
}

export function SharedWithMeList({
  receivedShares,
  highlightParlayId = null,
}: SharedWithMeListProps): ReactElement {
  const {
    sharedWithMe,
    unviewedCount,
    markViewed,
    fetchSharedWithMe,
    sharedWithMeLoading,
    sharedWithMeError,
  } = receivedShares;

  // App loaded these at boot; opening the tab refreshes them once
  useEffect(() => {
    fetchSharedWithMe();
  }, [fetchSharedWithMe]);

  // Scrolled to once, when its card first mounts (the list may load after the tab opens)
  const hasScrolledRef = useRef(false);
  const highlightRef = useCallback((card: HTMLDivElement | null) => {
    if (!card || hasScrolledRef.current) return;
    hasScrolledRef.current = true;
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  if (sharedWithMeLoading && sharedWithMe.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (sharedWithMeError) {
    return (
      <div className="bg-theme-danger/10 border border-theme-danger/30 rounded-lg p-4 text-theme-danger">
        Failed to load shared parlays: {sharedWithMeError}
      </div>
    );
  }

  if (sharedWithMe.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">📬</div>
        <h2 className="text-xl font-semibold text-theme-text-secondary mb-2">No Shared Parlays</h2>
        <p className="text-theme-text-secondary">
          When someone shares a parlay with you, it will appear here
        </p>
      </div>
    );
  }

  // Sort: unviewed first, then by date
  const sortedShares = [...sharedWithMe].sort((a, b) => {
    // Unviewed first
    if (a.viewedAt === null && b.viewedAt !== null) return -1;
    if (a.viewedAt !== null && b.viewedAt === null) return 1;
    // Then by date (newest first)
    return new Date(b.sharedAt).getTime() - new Date(a.sharedAt).getTime();
  });

  return (
    <div className="space-y-4">
      {unviewedCount > 0 && (
        <div className="text-sm text-theme-primary mb-2">
          {unviewedCount} new parlay{unviewedCount > 1 ? 's' : ''} shared with you
        </div>
      )}

      {sortedShares.map((share) => (
        <SharedParlayCard
          key={share.shareId}
          share={share}
          onView={markViewed}
          highlightRef={share.parlayId === highlightParlayId ? highlightRef : undefined}
        />
      ))}
    </div>
  );
}
